import {
  ApplicationInsights,
  ITelemetryItem,
} from "@microsoft/applicationinsights-web"
import { ReactPlugin } from "@microsoft/applicationinsights-react-js"
import { Role } from "./constants"

const filterDependencyTracking = (item: ITelemetryItem) => {
  if (item.baseType === "RemoteDependencyData") {
    return false
  }
  return true
}

type TelemetryContext = {
  org: string
  practitionerId: string
  role: "supervisor" | "practitioner" | "admin" | "unknown"
  isAdmin: boolean
}

const telemetryContext: TelemetryContext = {
  org: "",
  practitionerId: "",
  role: "unknown",
  isAdmin: false,
}

const getFormattedTimezone = () => {
  const offset = new Date().getTimezoneOffset() * -1
  const hours = Math.trunc(offset / 60)
  const remainingMinutes = Math.abs(offset % 60)
  const paddedHours = `${hours}`.padStart(2, "0")

  return `${paddedHours}:${`${remainingMinutes}`.padStart(2, "0")}`
}

export const reactPlugin = new ReactPlugin()

const connectionString =
  process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING?.trim() || ""

export const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
  },
})

if (connectionString) {
  appInsights.addTelemetryInitializer((envelope) => {
    if (!envelope.baseData) {
      return
    }

    envelope.baseData.properties = {
      ...envelope.baseData.properties,
      timezone: getFormattedTimezone(),
      epochTime: Date.now(),
      practitionerId: telemetryContext.practitionerId,
      role: telemetryContext.role,
      isAdmin: telemetryContext.isAdmin,
      organization: telemetryContext.org,
    }
  })
  appInsights.addTelemetryInitializer(filterDependencyTracking)
  appInsights.loadAppInsights()
}

const getUserRole = (role: string) => {
  switch (role) {
    case Role.WEB_ROLE:
      return "supervisor"
    case Role.ADMIN:
      return "admin"
    case Role.PRACTITIONER:
      return "practitioner"
    default:
      return "unknown"
  }
}

export const appInsightsWithOrg = (
  org: string,
  practitionerId: string,
  role?: string,
  isOrgAdmin?: boolean
) => {
  telemetryContext.org = org ?? ""
  telemetryContext.practitionerId = practitionerId ?? ""
  telemetryContext.role = getUserRole(role ?? "")
  telemetryContext.isAdmin = isOrgAdmin ?? false

  if (!connectionString) {
    return
  }

  if (telemetryContext.practitionerId) {
    appInsights.setAuthenticatedUserContext(
      telemetryContext.practitionerId,
      telemetryContext.org,
      false
    )
    return
  }

  appInsights.clearAuthenticatedUserContext()
}

export const clearAppInsightsContext = () => {
  telemetryContext.org = ""
  telemetryContext.practitionerId = ""
  telemetryContext.role = "unknown"
  telemetryContext.isAdmin = false

  if (!connectionString) {
    return
  }

  appInsights.clearAuthenticatedUserContext()
}
